/* eslint-disable @typescript-eslint/camelcase */
import { observable, action, computed } from 'mobx';
import reactionWithOldValue from '@/utils/reactionWithOldValue';
import { BigNumber } from 'bignumber.js';
import findLast from 'lodash/findLast';

import Axios from 'axios';
import {
  MarkPriceInfo,
  PrecisionMap,
  SymbolConfig,
  TickerInfo,
  FundingRateItem,
  MarkPriceMap,
  RiskFundItem,
  SymbolLeverageBracketItem,
  LeverageBracketItem,
  ScrollPageInfo,
  DepthMergeItem,
  SymbolPrecision,
} from './spotData';
import { API } from '../config/api';
import { PositionItem } from '@/models/spotPosition';
import { SCROLL_PAGINATION_LIMIT_DEFAULT } from '@/config/const';
import { createPrecisionView } from '@/utils/digits';
import { createSliderMarks } from '@/utils/fn';

const defaultTickerInfo = {
  a: '0.00',
  c: '0.00',
  h: '0.00',
  l: '0.00',
  o: '0.00',
  r: '0.00',
  s: '',
  t: 0,
  v: '0.00',
};

const defaultSymbolConfig: SymbolConfig = {
  buyCoin: '',
  buyCoinDisplayPrecision: 6,
  buyCoinPrecision: 6,
  depthPrecisionMerge: 3,
  hot: true,
  makerFee: '0.01',
  marketTakeBound: '0.05',
  minNotional: '',
  minPrice: '',
  minQty: '0.00001',
  multiplierDown: '0.5',
  multiplierUp: '1',
  onboardDate: 1666222300000,
  pricePrecision: 2,
  quantityPrecision: 5,
  sellCoin: 'BTC',
  sellCoinDisplayPrecision: 3,
  sellCoinPrecision: 6,
  setType: 1,
  state: 1,
  supportOrderType: 'LIMIT',
  supportTimeInForce: 'GTC',
  symbol: 'BTC_USDT',
  takerFee: '0.01',
  tradeSwitch: true,
  uids: '',
};

export default class Ticker {
  @observable currentSymbolIdentity?: string; // 交易对ID

  @observable currentSymbolTicker: TickerInfo;

  @observable symbolConfigList: SymbolConfig[];

  // @observable fundingRateList: FundingRateItem[];

  // @observable fundingRateListPageInfo: ScrollPageInfo;

  // @observable latestFundingRateInfo: FundingRateItem;

  @observable
  spotTickerList: TickerInfo[] = [];

  // @observable leverageBracketList: SymbolLeverageBracketItem[];

  @observable isFetchingSymbolConfigList: boolean;

  @observable isFetchingLeverageBracketList: boolean;

  @observable isFetchingTickerList: boolean;

  // @observable isFetchingFundRateList: boolean;

  // @observable isFetchingRiskFundList: boolean;

  // @observable isFetchingIndexPriceList: boolean;

  @observable hasOnboard: boolean;

  @observable coinList: string[];

  constructor() {
    this.currentSymbolIdentity = '';
    this.symbolConfigList = [];

    // this.fundingRateList = [];
    // this.fundingRateListPageInfo = {
    //   hasPrev: false,
    //   hasNext: false,
    // };
    // this.latestFundingRateInfo = {
    //   nextCollectionTime: 0,
    //   fundingRate: 0,
    //   symbol: '',
    //   collectionInternal: 0,
    // };

    this.currentSymbolTicker = defaultTickerInfo;
    this.spotTickerList = [];

    // this.leverageBracketList = [];
    this.isFetchingSymbolConfigList = false;
    this.isFetchingLeverageBracketList = false;
    this.isFetchingTickerList = false;
    // this.isFetchingFundRateList = false;
    // this.isFetchingRiskFundList = false;
    // this.isFetchingIndexPriceList = false;

    this.hasOnboard = true;

    this.coinList = [];

    reactionWithOldValue(
      () => this.currentSymbolConfig,
      (currentSymbolConfig, oldValue) => {
        // TODO
      },
    );
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  @action.bound
  setHasOnboard(value: boolean) {
    this.hasOnboard = value;
  }

  @action.bound
  setSymbolConfigList(data: SymbolConfig[]) {
    this.symbolConfigList = data;
  }

  @action.bound
  async fetchSymbolConfigList() {
    this.setLoadingState('isFetchingSymbolConfigList', true);

    const result = await Axios.get(API.SPOT_SYMBOL_CONFIG_LIST);

    if (Array.isArray(result)) {
      this.setSymbolConfigList(result);
    }

    this.setLoadingState('isFetchingSymbolConfigList', false);
  }

  @action.bound
  getSymbolConfig(symbol: string): SymbolConfig {
    return this.symbolConfigList.find((item: SymbolConfig) => symbol === item.symbol) || defaultSymbolConfig;
  }

  @action.bound
  getSymbolConfigByCoin(coin: string): SymbolConfig {
    if (coin === 'usdt') {
      return { sellCoinPrecision: 4, sellCoinDisplayPrecision: 4 } as SymbolConfig;
    }

    return this.symbolConfigList.find((item: SymbolConfig) => coin === item.sellCoin) || defaultSymbolConfig;
  }

  @computed get currentSymbolConfig(): SymbolConfig {
    return (
      this.symbolConfigList.find((item: SymbolConfig) => item.symbol === this.currentSymbolIdentity) ||
      defaultSymbolConfig
    );
  }

  @computed get precisionMap(): PrecisionMap {
    const result: PrecisionMap = {};
    const configList = this.symbolConfigList;
    const length = configList.length;

    for (let i = 0; i < length; i += 1) {
      const {
        symbol,
        buyCoinDisplayPrecision,
        sellCoinDisplayPrecision,
        buyCoinPrecision,
        sellCoinPrecision,
        pricePrecision,
      } = configList[i];

      result[symbol] = {
        buyCoinPrecision,
        buyCoinDisplayPrecision,
        sellCoinPrecision,
        sellCoinDisplayPrecision,
        price: pricePrecision,
        quantity: sellCoinDisplayPrecision,
      };
    }
    return result;
  }

  @action.bound
  getPrecision(symbol?: string): SymbolPrecision {
    const { precisionMap } = this;

    if (symbol && Reflect.has(precisionMap, symbol)) {
      return precisionMap[symbol];
    }

    return {
      buyCoinPrecision: 4,
      buyCoinDisplayPrecision: 4,
      sellCoinPrecision: 4,
      sellCoinDisplayPrecision: 4,
      price: 4,
      quantity: 4,
    };
  }

  @computed get currentSymbolPrecision(): SymbolPrecision {
    const { currentSymbolIdentity, precisionMap } = this;

    if (currentSymbolIdentity && Reflect.has(precisionMap, currentSymbolIdentity)) {
      return precisionMap[currentSymbolIdentity];
    }

    return {
      buyCoinPrecision: 4,
      buyCoinDisplayPrecision: 4,
      sellCoinPrecision: 4,
      sellCoinDisplayPrecision: 4,
      price: 4,
      quantity: 4,
    };
  }

  @action.bound
  setCurrentSymbolIdentity(data: string) {
    this.currentSymbolIdentity = data?.toUpperCase();
    sessionStorage.setItem('spot_symbol', data || '');
  }

  @computed get latestMarkPrice() {
    // @ts-ignore
    return this.markPriceMap[this.currentSymbolIdentity] || '0.00';
  }

  // 所有交易对的最新标记价格
  @computed get markPriceMap() {
    return this.spotTickerList.map(item => ({
      symbol: item.s,
      price: item.c,
    }));
  }

  @computed get availablePrice() {
    const markPrice = this.latestMarkPrice;
    return new BigNumber(markPrice).gt(0) ? markPrice : '0';
  }

  // 资金费率
  // @action.bound
  // setLatestFundingRateInfo(data: FundingRateItem) {
  //   this.latestFundingRateInfo = data;
  // }

  // @action.bound
  // async fetchLatestFundingRate({ symbol }: { symbol: string }) {
  //   const result = await Axios.get(API.TICKER_FUNDING_RATE, {
  //     params: {
  //       symbol,
  //     },
  //   });
  //
  //   if (result) {
  //     this.setLatestFundingRateInfo(result as any);
  //   }
  // }

  // @action.bound
  // setFundingRateList(data: FundingRateItem[]) {
  //   this.fundingRateList = data;
  // }

  // @action.bound
  // setFundingRateListPageInfo(data: ScrollPageInfo) {
  //   const origin = { ...this.fundingRateListPageInfo };
  //   this.fundingRateListPageInfo = { ...origin, ...data };
  // }

  // @action.bound
  // async fetchFundingRateList({
  //   symbol,
  //   direction,
  //   limit = SCROLL_PAGINATION_LIMIT_DEFAULT,
  //   id,
  // }: {
  //   symbol: string;
  //   direction: ScrollPaginationDirection;
  //   limit?: number;
  //   id?: number;
  // }) {
  //   this.setLoadingState('isFetchingFundRateList', true);
  //
  //   const config: any = {
  //     params: {
  //       symbol,
  //       direction,
  //       limit,
  //       id,
  //     },
  //   };
  //
  //   // if (symbol) {
  //   //   config.baseURL = getBaseUrlBySymbol(symbol);
  //   // }
  //
  //   const result: any = await Axios.get(API.SPOT_TICKER_FUNDING_RATE_RECORD, config);
  //
  //   if (result) {
  //     const { hasNext, hasPrev, items } = result;
  //
  //     this.setFundingRateList(items || []);
  //     this.setFundingRateListPageInfo({ hasPrev, hasNext });
  //   }
  //
  //   this.setLoadingState('isFetchingFundRateList', false);
  // }

  @action.bound
  setCurrentSymbolTicker(data: TickerInfo) {
    this.currentSymbolTicker = data;
  }

  @action.bound
  async fetchCurrentSymbolTicker(params: { symbol: string }) {
    const { symbol } = params;

    if (!symbol) return;

    const result: any = await Axios.get(API.SPOT_TICKER_CURRENT_SYMBOL, {
      params: {
        symbol,
      },
    });

    if (result) {
      this.setCurrentSymbolTicker(result);
    } else {
      this.setCurrentSymbolTicker(defaultTickerInfo);
    }
  }

  @action
  setSpotTickerList(data: TickerInfo[]) {
    // 标准化交易对格式：统一转为大写，避免接口返回小写和socket推送大写不一致的问题
    this.spotTickerList = data.map((item: TickerInfo) => ({
      ...item,
      s: item.s ? item.s.toUpperCase() : item.s,
      symbol: item.s ? item.s.toUpperCase() : item.s,
    }));
  }

  @action.bound
  async fetchTickerList() {
    this.setLoadingState('isFetchingTickerList', true);
    const result = await Axios.get(API.SPOT_TICKER_LIST);

    if (Array.isArray(result)) {
      this.setSpotTickerList(result);
    }
    this.setLoadingState('isFetchingTickerList', false);
  }

  @computed get symbolCurrentPriceMap() {
    const result: { [key: string]: string | number } = {};
    this.spotTickerList.forEach((item: TickerInfo) => {
      result[item.s] = item.c;
    });

    return result;
  }

  // ws 更新行情列表
  @action.bound
  setSymbolTickerSpotByWS(data: { [key: string]: TickerInfo }) {
    const originList = [...this.spotTickerList];
    const currentSymbolIdentity = this.currentSymbolIdentity;

    Object.entries(data).forEach(([key, ticker]: any) => {
      // 使用 ticker.s 来查找，确保按 symbol 去重，而不是按 key
      // 大小写不一致，需要转换为大写再进行比较
      const index = originList.findIndex((item: TickerInfo) => ticker.s.toUpperCase() === item.s.toUpperCase());
      if (index > -1) {
        const { s } = ticker;

        originList[index] = ticker;

        if (s.toUpperCase() === currentSymbolIdentity?.toUpperCase()) {
          this.setCurrentSymbolTicker(ticker);
        }
      } else {
        originList.push(ticker);
      }
    });
    
    // 在循环外部统一更新，避免重复更新
    this.setSpotTickerList(originList);
  }

  // 杠杆分层
  // @action.bound
  // setLeverageBracketList(data: SymbolLeverageBracketItem[]) {
  //   this.leverageBracketList = data;
  // }

  // @action.bound
  // async fetchLeverageBracketList() {
  //   this.setLoadingState('isFetchingLeverageBracketList', true);
  //   const result: SymbolLeverageBracketItem[] = await Axios.get(API.SPOT_LEVERAGE_BRACKET_LIST);
  //
  //   if (result) {
  //     this.setLeverageBracketList(result);
  //   }
  //   this.setLoadingState('isFetchingLeverageBracketList', false);
  // }

  // @computed get currentSymbolLeverageBrackets(): LeverageBracketItem[] {
  //   const symbol = this.currentSymbolIdentity;
  //   const result = this.leverageBracketList.find(item => item.symbol === symbol);
  //
  //   if (result) {
  //     return result.leverageBrackets;
  //   }
  //
  //   return [];
  // }

  // @computed get leverageSliderConfig(): { maxMultiple: number; minMultiple: number; marks: any } {
  //   const firsLv = this.currentSymbolLeverageBrackets[0];
  //   const max = firsLv?.maxLeverage || 100;
  //   const min = firsLv?.minLeverage || 1;
  //
  //   return {
  //     maxMultiple: new BigNumber(max).toNumber(),
  //     minMultiple: new BigNumber(min).toNumber(),
  //     marks: createSliderMarks(max, min),
  //   };
  // }

  // // 获取持仓所处的的风险档位
  // @action.bound
  // getPositionBracket(position: PositionItem): LeverageBracketItem {
  //   const { symbol, positionSize, entryPrice } = position;
  //   const { getSymbolConfig, leverageBracketList } = this;
  //   const currentSymbolConfig = getSymbolConfig(symbol);
  //   const { contractSize } = currentSymbolConfig;
  //
  //   if (contractSize) {
  //     const symbolBrackets = leverageBracketList.find(item => item.symbol === symbol)?.leverageBrackets;
  //     let nominalValueBN: any = new BigNumber(0);
  //
  //     if (new BigNumber(positionSize).gt(0)) {
  //       nominalValueBN = new BigNumber(entryPrice).times(positionSize).times(contractSize);
  //     }
  //
  //     return symbolBrackets?.find(item => nominalValueBN.lte(item.maxNominalValue)) || ({} as LeverageBracketItem);
  //   }
  //
  //   return {} as LeverageBracketItem;
  // }

  // 获取杠杆所处的的最高风险档位
  // @action.bound
  // getLeverageMaxBracket(leverage: number): LeverageBracketItem | undefined {
  //   const currentSymbolLeverageBrackets = this.currentSymbolLeverageBrackets;
  //   return findLast(currentSymbolLeverageBrackets, item => new BigNumber(leverage).lte(item.maxLeverage));
  // }

  // 当前交易对档位深度
  @computed get currentSymbolDepthPrecisionMergeList(): DepthMergeItem[] {
    const { depthPrecisionMerge, pricePrecision } = this.currentSymbolConfig;
    const base = new BigNumber(createPrecisionView(pricePrecision));
    const result = [];

    if (depthPrecisionMerge && !base.isNaN()) {
      for (let i = 1; i <= depthPrecisionMerge; i += 1) {
        const label = base.times(10 ** (i - 1));
        const precision = label.toFixed().split('.')[1]?.length || 0;
        const item = { label: label.toFixed(), value: i, base: label.toNumber(), precision };
        result.push(item);
      }
    }

    return result;
  }

  @action
  setCoinList(data: string[]) {
    this.coinList = data;
  }

  @action.bound
  async fetchCoinList() {
    const result = await Axios.get(API.SPOT_SYMBOL_COIN_LIST);

    if (Array.isArray(result)) {
      this.setCoinList(result);
    }
  }
}
